import { materialNumber, materialStringMap, type MaterialDto } from "@models/data/materialDto";

export type MaterialWorkflow = "Specular" | "Metallic";
export type MaterialSurface = "Opaque" | "Transparent";
export type MaterialShaderVariantId = string;
export type MaterialShaderFamily =
  | "lit"
  | "simple-lit"
  | "unlit"
  | "metal"
  | "plastic"
  | "glass"
  | "wood"
  | "fabric-leather"
  | "car-paint"
  | "rubber"
  | "effect";

export interface MaterialShaderVariant {
  readonly id: MaterialShaderVariantId;
  readonly shaderName: string;
  /**
   * What the shader dropdown shows. Taken from the desktop client's own
   * authored `MaterialLibraryModel.m_shaders` list
   * (`CampfireScenes/Assets/Campfire/Content/Models/DesktopEditor.prefab`),
   * which is where the names come from — `ShaderInfo.Name` is a hand-written
   * label, not the shader's path. Hence "PBR Lit" for `Campfire/Lit` and, yes,
   * "GlassFresnell" (sic) for `Shader Graphs/GlassDifraction`.
   */
  readonly displayName: string;
  readonly family: MaterialShaderFamily;
  /**
   * The shader's OWN surface type, and the only thing that decides transparency
   * for a shader the material editor cannot configure.
   *
   * ⚠ The stored `Surface` field cannot answer this. `MaterialData.FromMaterial`
   * writes it under `HasProperty("_Surface") && IsConfigurableMaterial()`
   * (`MaterialData.cs:458`), and `IsConfigurableMaterial` tests the shader NAME
   * against `Campfire/Lit` / `Shader Graphs/CampfireLit` — so every library
   * shadergraph row carries the C# struct default 0 = Opaque, INCLUDING the six
   * baked-transparent ones. Deriving transparency from that field alone renders
   * all the glass solid; deriving it from a coarse family bucket instead is what
   * the port did, and it swept `Reflector` and `ARUXShader` (both baked Opaque)
   * into transparency.
   *
   * ⚠ It is taken from the GRAPH, not from the `.mat`, and that is safe here
   * rather than approximate — measured across all 23 library graphs: every graph
   * whose surface is Transparent sets `m_AllowMaterialOverride: false`, so its
   * baked value cannot be overridden per material, and every graph that DOES
   * allow the override bakes Opaque. `GlassTinted.mat` serializes `_Surface: 0`
   * and still renders transparent for exactly that reason. Regenerate with
   * `scratchpad/harness-shader/extract-baked.mjs`.
   */
  readonly bakedSurface: MaterialSurface;
  readonly supportsSpecularWorkflow: boolean;
  readonly supportsMetallicWorkflow: boolean;
  readonly isConfigurable: boolean;
  /**
   * Whether the shader appears in the inspector's dropdown. A material may
   * REFERENCE a shader that is not offered (Unity logs "was not found. Add … to
   * the Shaders list" and leaves the dropdown index alone — `MaterialViewModel.cs:868`),
   * so resolution and offering are two different questions.
   */
  readonly isOffered: boolean;
}

export interface MaterialKeywords {
  readonly alphaTest: boolean;
  readonly emission: boolean;
  readonly metallicSpecGlossMap: boolean;
  readonly occlusionMap: boolean;
  readonly specGlossMap: boolean;
  readonly specularSetup: boolean;
  readonly surfaceTransparent: boolean;
}

export const DEFAULT_MATERIAL_SHADER = "Campfire/Lit";

const K_SHADER_ALIASES: Readonly<Record<string, string>> = {
  "Universal Render Pipeline/Lit (Advanced)": DEFAULT_MATERIAL_SHADER,
  "Universal Render Pipeline/Lit": DEFAULT_MATERIAL_SHADER,
  "Shader Graphs/CampfireLit": DEFAULT_MATERIAL_SHADER,
};

/**
 * The shader list, in the desktop client's own dropdown ORDER — the 20 entries
 * of `MaterialLibraryModel.m_shaders`, then the two that scene materials can
 * reference but the dropdown does not offer.
 */
const K_SHADER_VARIANTS: ReadonlyArray<MaterialShaderVariant> = [
  {
    id: "campfire-lit",
    shaderName: DEFAULT_MATERIAL_SHADER,
    displayName: "PBR Lit",
    family: "lit",
    // Configurable, so the stored `Surface` field wins and this is only the
    // default a material with no stored value falls back to.
    bakedSurface: "Opaque",
    supportsSpecularWorkflow: true,
    supportsMetallicWorkflow: true,
    isConfigurable: true,
    isOffered: true,
  },
  graphVariant("caliper-carbon-fiber", "Shader Graphs/CaliperCarbonFiber", "plastic", "Opaque", "CaliperCarbonFiber"),
  graphVariant("car-paint", "Shader Graphs/CarPaint", "car-paint", "Opaque", "CarPaint"),
  graphVariant("fabric-suede-leather", "Shader Graphs/FabricSuedeLeather", "fabric-leather", "Opaque", "FabricSuedeLeather"),
  graphVariant("glass-difraction", "Shader Graphs/GlassDifraction", "glass", "Transparent", "GlassFresnell"),
  graphVariant("glass-simple", "Shader Graphs/GlassSimple", "glass", "Transparent", "GlassSimple"),
  graphVariant("glass-dirt", "Shader Graphs/GlassDirt", "glass", "Transparent", "GlassDirt"),
  graphVariant("metal", "Shader Graphs/Metal", "metal", "Opaque", "Metal"),
  graphVariant("metal-normal", "Shader Graphs/MetalNormal", "metal", "Opaque", "MetalNormal"),
  graphVariant("metal-simple", "Shader Graphs/MetalSimple", "metal", "Opaque", "MetalSimple"),
  graphVariant("plastic-dirt", "Shader Graphs/PlasticDirt", "plastic", "Opaque", "PlasticDirt"),
  graphVariant("plastic-normal", "Shader Graphs/PlasticNormal", "plastic", "Opaque", "PlasticNormal"),
  graphVariant("plastic-simple", "Shader Graphs/PlasticSimple", "plastic", "Opaque", "PlasticSimple"),
  graphVariant("plastic-transparent-dirt", "Shader Graphs/PlasticTransparentDirt", "plastic", "Transparent", "PlasticTransparentDirt"),
  graphVariant("plastic-transparent-simple", "Shader Graphs/PlasticTransparentSimple", "plastic", "Transparent", "PlasticTransparentSimple"),
  graphVariant("reflector", "Shader Graphs/Reflector", "effect", "Opaque", "Reflector"),
  graphVariant("rubber", "Shader Graphs/Rubber", "rubber", "Opaque", "Rubber"),
  graphVariant("campfire-unlit", "Shader Graphs/CampfireUnlit", "unlit", "Opaque", "Unlit"),
  graphVariant("wood", "Shader Graphs/Wood", "wood", "Opaque", "Wood"),
  {
    id: "campfire-simple-lit",
    shaderName: "Campfire/Simple Lit",
    displayName: "Lit",
    family: "simple-lit",
    // NOT configurable (`IsConfigurableMaterial` names only `Campfire/Lit` and
    // `Shader Graphs/CampfireLit`), so Unity never writes its `Surface` and this
    // baked value is the whole answer.
    bakedSurface: "Opaque",
    supportsSpecularWorkflow: true,
    supportsMetallicWorkflow: false,
    isConfigurable: false,
    isOffered: true,
  },

  // Resolvable but NOT offered — upstream's dropdown does not list them.
  graphVariant("ghost", "Shader Graphs/Ghost", "effect", "Transparent", "Ghost", false),
  graphVariant("arux", "Shader Graphs/ARUXShader", "effect", "Opaque", "ARUXShader", false),
];

const K_VARIANT_BY_SHADER = new Map(
  K_SHADER_VARIANTS.map((variant) => [variant.shaderName, variant]),
);

/**
 * The dropdown's options, in the desktop client's order.
 *
 * `current` keeps a material whose shader is NOT offered selectable rather than
 * silently re-pointing it at the first entry — Unity leaves its dropdown index
 * untouched in that case and warns (`MaterialViewModel.InitShaderIndex`), and a
 * `<select>` with no matching option would otherwise render blank and rewrite
 * the row on the next change event.
 */
export function materialShaderOptions(
  current?: string | null,
): ReadonlyArray<{ value: string; label: string }> {
  const options = K_SHADER_VARIANTS.filter((variant) => variant.isOffered).map((variant) => ({
    value: variant.shaderName,
    label: variant.displayName,
  }));
  const normalized = normalizeMaterialShaderName(current);
  if (current && !options.some((option) => option.value === normalized)) {
    const variant = resolveMaterialShaderVariant(normalized);
    options.push({ value: normalized, label: variant.displayName });
  }
  return options;
}

export function normalizeMaterialShaderName(shaderName: string | null | undefined): string {
  if (!shaderName) return DEFAULT_MATERIAL_SHADER;
  return K_SHADER_ALIASES[shaderName] ?? shaderName;
}

export function resolveMaterialShaderVariant(
  shaderName: string | null | undefined,
): MaterialShaderVariant {
  const normalized = normalizeMaterialShaderName(shaderName);
  return K_VARIANT_BY_SHADER.get(normalized) ?? inferGraphVariant(normalized);
}

/**
 * Is this material transparent? — Unity's own answer, which is NOT "what does
 * the stored `Surface` field say".
 *
 * Unity reads `_Surface` only when the shader declares it AND the shader is one
 * of the two configurable ones (`MaterialUtil.SetMaterialKeywords`, and the
 * matching guard on the write side at `MaterialData.cs:458`). For every other
 * shader the surface is the shader's own, which for the library shadergraphs is
 * baked and — measured across all 23 — unoverridable wherever it is Transparent.
 *
 * ⚠ Do not reintroduce a family test here. `glass` and `effect` are display
 * buckets: `effect` holds `Reflector` and `ARUXShader`, which are baked OPAQUE,
 * and sweeping them into transparency turned a solid orange safety reflector
 * into a see-through one that wrote no depth.
 */
export function isMaterialSurfaceTransparent(
  variant: MaterialShaderVariant,
  storedSurface: MaterialSurface,
): boolean {
  const surface = variant.isConfigurable ? storedSurface : variant.bakedSurface;
  return surface === "Transparent";
}

export function materialWorkflowFromDto(dto: MaterialDto): MaterialWorkflow {
  return materialNumber(dto.WorkflowMode, 1) === 1 ? "Metallic" : "Specular";
}

export function materialSurfaceFromDto(dto: MaterialDto): MaterialSurface {
  return materialNumber(dto.Surface, 0) === 1 ? "Transparent" : "Opaque";
}

export function deriveMaterialKeywords(input: {
  readonly shaderName?: string;
  readonly workflow: MaterialWorkflow;
  readonly surface: MaterialSurface;
  readonly emissionColorAlpha: number;
  readonly textureIds: Readonly<Record<string, string>>;
}): MaterialKeywords {
  const variant = resolveMaterialShaderVariant(input.shaderName);
  // ⚠ `_SPECULAR_SETUP` is CONFIGURABLE-shader only, not "the row says
  // Specular". `MaterialUtil.SetMaterialKeywords` reads `_WorkflowMode` behind
  // `material.HasProperty("_WorkflowMode") && material.IsConfigurableMaterial()`
  // (`MaterialUtil.cs:170-186`), and no shadergraph declares that property — so
  // every graph is metallic-setup whatever its row holds. And its row usually
  // holds 0 (= Specular) by accident: `MaterialData.FromMaterial` skips writing
  // `WorkflowMode` under the same guard, so the field is simply never set.
  // Reading it as specular there strips the metallic input from most of the
  // shipped library.
  const isSpecular = variant.isConfigurable && input.workflow === "Specular";
  return {
    alphaTest: true,
    emission: input.emissionColorAlpha > 0 || Boolean(input.textureIds._EmissionMap),
    metallicSpecGlossMap: !isSpecular && Boolean(input.textureIds._MetallicGlossMap),
    occlusionMap: Boolean(input.textureIds._OcclusionMap),
    specGlossMap: isSpecular && Boolean(input.textureIds._SpecGlossMap),
    specularSetup: isSpecular,
    surfaceTransparent: input.surface === "Transparent",
  };
}

export function materialTextureIds(dto: MaterialDto): Readonly<Record<string, string>> {
  return materialStringMap(dto.Textures);
}

function graphVariant(
  id: string,
  shaderName: string,
  family: MaterialShaderFamily,
  bakedSurface: MaterialSurface,
  displayName?: string,
  isOffered = true,
): MaterialShaderVariant {
  return {
    id,
    shaderName,
    displayName: displayName ?? readableShaderLabel(shaderName),
    family,
    bakedSurface,
    supportsSpecularWorkflow: family !== "unlit",
    supportsMetallicWorkflow: family !== "unlit" && family !== "simple-lit",
    isConfigurable: false,
    isOffered,
  };
}

/** Last-resort label for a shader nobody authored a name for. */
function readableShaderLabel(shaderName: string): string {
  return shaderName.replace(/^Shader Graphs\//, "").replace(/^Campfire\//, "");
}

/**
 * An unknown shader — a CAD import, a customer shader, a newer desktop build.
 *
 * Its surface FAILS OPEN to Opaque, matching the C# struct default that Unity
 * itself would carry for such a row, so an unrecognised shader renders solid
 * rather than see-through. The opposite default is the worse failure: a
 * transparent material that should be solid is merely wrong, while a solid one
 * forced transparent stops writing depth and drops out of the scene's sorting.
 */
function inferGraphVariant(shaderName: string): MaterialShaderVariant {
  return graphVariant(
    slugify(shaderName),
    shaderName,
    inferShaderFamily(shaderName),
    "Opaque",
    readableShaderLabel(shaderName),
    false,
  );
}

function inferShaderFamily(shaderName: string): MaterialShaderFamily {
  const lower = shaderName.toLowerCase();
  if (lower.includes("unlit")) return "unlit";
  if (lower.includes("simple lit") || lower.includes("simplelit")) return "simple-lit";
  if (lower.includes("glass") || lower.includes("xray") || lower.includes("ghost")) return "glass";
  if (lower.includes("metal") || lower.includes("chrome") || lower.includes("aluminum")) return "metal";
  if (lower.includes("plastic") || lower.includes("carbon")) return "plastic";
  if (lower.includes("wood") || lower.includes("maple") || lower.includes("mahogany")) return "wood";
  if (lower.includes("fabric") || lower.includes("suede") || lower.includes("leather")) {
    return "fabric-leather";
  }
  if (lower.includes("carpaint") || lower.includes("car paint")) return "car-paint";
  if (lower.includes("rubber")) return "rubber";
  if (shaderName.startsWith("Shader Graphs/")) return "effect";
  return "lit";
}

function slugify(value: string): string {
  return value
    .replace(/^Shader Graphs\//, "")
    .replace(/^Campfire\//, "campfire-")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
}
